import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListFilter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_SPORT_CODES } from '@low6-pickem/sports-config';

export type SportFilterValue = 'ALL' | (typeof SUPPORTED_SPORT_CODES)[number];

interface SportFilterProps {
  selectedSport: SportFilterValue;
  onSportChange: (sport: SportFilterValue) => void;
}

interface SportOption {
  value: SportFilterValue;
  label: string;
  svgSrc: string;
}

const SPORT_OPTIONS: SportOption[] = [
  { value: 'ALL', label: 'All', svgSrc: '/all-sports-icon.svg' },
  { value: 'NBA', label: 'NBA', svgSrc: '/basketball-icon.svg' },
  { value: 'NFL', label: 'NFL', svgSrc: '/football-icon.svg' },
  { value: 'NCAAMB', label: 'NCAAB', svgSrc: '/basketball-icon.svg' },
];

const SportFilter: React.FC<SportFilterProps> = ({ selectedSport, onSportChange }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isFiltered = selectedSport !== 'ALL';

  return (
    <div className="mb-4">
      {/* Centered title with filter icon on the right */}
      <div className="relative flex items-center justify-center mb-2">
        <h2
          className="text-white font-gilroy font-bold text-center"
          style={{ fontSize: '16px', lineHeight: '20px' }}
        >
          {t('pages.selectContest', 'Select a contest')}
        </h2>
        <button
          onClick={() => setIsExpanded(prev => !prev)}
          className={`absolute right-0 p-2 rounded-lg transition-colors ${
            isExpanded || isFiltered
              ? 'text-pickem-primary'
              : 'text-text-secondary hover:text-white'
          }`}
          aria-label="Filter by sport"
          aria-expanded={isExpanded}
        >
          <ListFilter size={20} />
        </button>
      </div>

      {/* Expandable pill row below the title */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="bg-white/5 rounded-lg px-3 py-2.5 flex gap-2 justify-center overflow-x-auto scrollbar-hide">
              {SPORT_OPTIONS.map(option => {
                const isActive = selectedSport === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => onSportChange(option.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors border flex-shrink-0 ${
                      isActive
                        ? 'bg-white text-gray-900 border-white'
                        : 'bg-transparent text-text-secondary border-text-secondary/40 hover:border-text-secondary'
                    }`}
                  >
                    <img
                      src={option.svgSrc}
                      alt={option.label}
                      className={`w-4 h-4 ${isActive ? 'brightness-0' : 'opacity-70'}`}
                    />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SportFilter;
